import { useEffect, useRef, useState } from "react";
import {
  getCategories,
  getCoursesByCategory,
  getMostViewedVideos,
} from "../services/homeService";
import type {
  Category,
  CoursesByCategory,
  MostViewedVideo,
} from "../types/home";

interface HomeData {
  categories: Category[];
  courses: CoursesByCategory;
  mostViewedVideos: MostViewedVideo[];
}

/**
 * Loads the categories (+ their first course) and the most-viewed videos once a
 * token is available. Fetching is guarded by a ref so it runs exactly once,
 * even if the token reference changes across renders.
 *
 * `onLoaded` is read through a ref so a fresh callback identity on every parent
 * render does not re-trigger the effect.
 */
export const useHomeData = (
  token: string | null,
  onLoaded: (status: boolean) => void
): HomeData => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [courses, setCourses] = useState<CoursesByCategory>({});
  const [mostViewedVideos, setMostViewedVideos] = useState<MostViewedVideo[]>(
    []
  );

  const hasFetched = useRef(false);
  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;

  useEffect(() => {
    if (!token || hasFetched.current) {
      return;
    }
    hasFetched.current = true;

    const loadCategories = async (): Promise<void> => {
      try {
        const { response: fetchedCategories } = await getCategories();

        const entries = await Promise.all(
          fetchedCategories.map(
            async (category) =>
              [category.id, (await getCoursesByCategory(category.id)).response[0]] as const
          )
        );

        setCourses(Object.fromEntries(entries) as CoursesByCategory);
        setCategories(fetchedCategories);
        onLoadedRef.current(true);
      } catch (error) {
        console.warn("Landing Category error", error);
      }
    };

    const loadMostViewed = async (): Promise<void> => {
      try {
        const { response } = await getMostViewedVideos();
        setMostViewedVideos(response);
      } catch (error) {
        console.warn("Most viewed videos error", error);
      }
    };

    void loadCategories();
    void loadMostViewed();
  }, [token]);

  return { categories, courses, mostViewedVideos };
};
